import React from 'react'
import Box from '@material-ui/core/Box'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import DeleteOutlinedIcon from 'components/material-symbol/icons/Delete'
import SyncAltIcon from 'components/material-symbol/icons/SyncAlt'
import EventBusyOutlinedIcon from 'components/material-symbol/icons/EventBusy'
import SectionEmptyState from 'components/section-empty-state'
import { makeStyles } from '@material-ui/core/styles'
import { DateTime } from 'luxon'
import BlockTimeDestroyDialog from '../../blocked-times/block-time-manager/block-time-destroy-dialog'
import {
  availabilityDot,
  isGoogleSyncedBlock,
  SYNCED_BLOCK_TOOLTIP,
} from './availability-colors'
import { formatBlockRangeDate, formatTime12 } from './utils'

const useStyles = makeStyles(theme => ({
  list: {
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: theme.palette.common.white,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid #E0E0E0',
    '&:last-child': { borderBottom: 'none' },
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: 2,
    ...availabilityDot('blocked'),
    boxSizing: 'border-box',
    display: 'inline-block',
    flexShrink: 0,
  },
  dotSynced: availabilityDot('synced'),
  date: {
    fontWeight: 500,
    color: '#172024',
  },
  note: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
  },
  meta: {
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
    marginLeft: 'auto',
    textAlign: 'right',
  },
  actions: {
    marginLeft: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  syncedIndicator: {
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
  },
  deleteButton: {
    padding: 6,
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid #C4C7C9',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: '#F4F6F8',
      borderColor: theme.palette.primary.main,
    },
    '& .MuiSvgIcon-root': {
      width: 18,
      height: 18,
      fontSize: 18,
    },
  },
}))

const formatBlockTime = (startISO, endISO, allDay) => {
  if (allDay) return 'All day'
  return `${formatTime12(startISO)} – ${formatTime12(endISO)}`
}

const BlocksList = ({ blocks, onChanged, emptyAction }) => {
  const classes = useStyles()
  if (!blocks || blocks.length === 0) {
    return (
      <SectionEmptyState
        Icon={EventBusyOutlinedIcon}
        title="No Blocked Times"
        caption="Mark time as not bookable for vacations, time off, or other commitments so students can't book during those windows."
        action={emptyAction}
      />
    )
  }

  const sorted = [...blocks].sort(
    (a, b) =>
      DateTime.fromISO(a.startTime).toMillis() -
      DateTime.fromISO(b.startTime).toMillis(),
  )

  return (
    <Box className={classes.list}>
      {sorted.map(block => {
        const timeText = formatBlockTime(
          block.startTime,
          block.endTime,
          block.allDay,
        )
        const note = block.blockNote
        const isSynced = isGoogleSyncedBlock(block)
        const cardKey = isSynced
          ? `synced-${block.startTime}-${block.endTime}`
          : block.id
        return (
          <Box key={cardKey} className={classes.row}>
            <span
              className={`${classes.dot} ${isSynced ? classes.dotSynced : ''}`}
              aria-hidden
            />
            <Box>
              <Typography variant="body2" className={classes.date}>
                {formatBlockRangeDate(block.startTime, block.endTime)}
              </Typography>
              <Typography variant="caption" className={classes.note}>
                {block.coachFacility?.name || 'All facilities'}
                {note ? ` · ${note}` : ''}
              </Typography>
            </Box>
            <Typography variant="body2" className={classes.meta}>
              {timeText}
            </Typography>
            <Box className={classes.actions}>
              {isSynced ? (
                <Tooltip title={SYNCED_BLOCK_TOOLTIP} placement="left">
                  <Box className={classes.syncedIndicator}>
                    <SyncAltIcon fontSize="small" />
                  </Box>
                </Tooltip>
              ) : (
                <BlockTimeDestroyDialog
                  blockTime={block}
                  onSuccess={onChanged}
                  trigger={
                    <IconButton
                      aria-label="delete blocked time"
                      className={classes.deleteButton}
                    >
                      <DeleteOutlinedIcon fontSize="small" />
                    </IconButton>
                  }
                />
              )}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default BlocksList
